import { Observable, of } from 'rxjs';
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { RequestDataAccessServiceConfig } from '../request-data-access.service';
import { Credentials, CredentialsFactory } from 'authorization';
import { from } from 'rxjs';
import { catchError, concatMap, map, mergeMap } from 'rxjs/operators';

export class CouchDbEntitiesList {
  docs: CouchDbEntity[];
  checkType(obj: any): obj is CouchDbEntitiesList {
    const hasDocs = Array.isArray(obj.docs);
    const docsElementsHaveCorrectType = hasDocs && (obj.docs.length === 0 || new CouchDbEntity().checkType(obj.docs[0]));
    return hasDocs && docsElementsHaveCorrectType;
  }
}

export class CouchDbEntity {
  _deleted: boolean;
  _id: string;
  _rev: string | undefined;
  checkType(obj: any): obj is CouchDbEntity {
    if (obj === null) {
      return false;
    }
    return !!obj._id && !!obj._rev;
  }
}

export class CouchDbFilterCondition<T> {
  private _field: string;
  private _value: string;
  private _mode: 'FIRST_LETTERS' | 'PARTIAL' | 'EXACT';

  constructor(field: (x: T) => any, value: string, mode: 'FIRST_LETTERS' | 'PARTIAL' | 'EXACT') {
    this._field = field.toString().split('.').slice(1).join('.');
    this._mode = mode;
    this._value = value;
  }

  get field(): string {
    return this._field;
  }

  get value(): string {
    return this._value;
  }

  get mode(): 'FIRST_LETTERS' | 'PARTIAL' | 'EXACT' {
    return this._mode;
  }
}

export class CouchDbFilter<T> {
  constructor(private _filter: CouchDbFilterCondition<T>[], private _limit: number) {}

  getMangoQuery(): any {
    let result: any = { selector: {}, limit: this._limit };
    this._filter.forEach((f) => {
      switch (f.mode) {
        case 'EXACT':
          result.selector[f.field] = { $regex: '(*UTF8)(?i)^' + f.value + '$' };
          break;
        case 'FIRST_LETTERS':
          result.selector[f.field] = { $regex: '(*UTF8)(?i)^' + f.value + '.*' };
          break;
        case 'PARTIAL':
          result.selector[f.field] = { $regex: '(*UTF8)(?i)' + f.value };
          break;
      }
    });
    return result;
  }
}

export class CouchDbRepository<T> {
  constructor(
    private keyExtractor: (x: T) => string,
    private factory: (src: any) => T,
    private databaseName: string,
    private httpClient: HttpClient,
    private config: RequestDataAccessServiceConfig,
    private credentialsFactory: CredentialsFactory,
  ) {}

  getAll$(asAnonymous: boolean = false): Observable<T> {
    const queryUrl = this.config.getServerURL() + this.databaseName + '/_find';
    return CouchDbRepository.authorizeExecute(
      this.credentialsFactory,
      (options) => {
        return this.httpClient.post(queryUrl, { selector: {}, limit: 10000 }, options).pipe(
          mergeMap((response: any) => {
            if (new CouchDbEntitiesList().checkType(response)) {
              if (response.docs.length === 0) {
                return from([]);
              } else {
                return from(response.docs.map((doc) => this.factory(doc)));
              }
            } else {
              throw new Error('');
            }
          }),
        );
      },
      asAnonymous,
    );
  }

  store$(entity: T): Observable<string | null> {
    const getQueryUrl = this.config.getServerURL() + this.databaseName + '/' + this.keyExtractor(entity);
    const storeQueryUrl = this.config.getServerURL() + this.databaseName + '/' + this.keyExtractor(entity);
    return CouchDbRepository.authorizeExecute(this.credentialsFactory, (options) => {
      return this.httpClient.get(getQueryUrl, options).pipe(
        catchError((err: HttpErrorResponse) => {
          if (err.error.error === 'not_found') {
            return of(null);
          } else {
            throw err;
          }
        }),
        mergeMap((x) => {
          let cur = new CouchDbEntity();
          if (cur.checkType(x)) {
            cur = x;
          } else if (x === null) {
            cur._id = this.keyExtractor(entity);
          }
          Object.assign(cur, this.factory(entity));
          if (cur._rev === '') {
            cur._rev = undefined;
          }
          return this.httpClient.put(storeQueryUrl, cur, options).pipe(
            map((response: any) => {
              return response.ok ? response.id : null;
            }),
          );
        }),
      );
    });
  }

  get$(id: string): Observable<T | null> {
    const queryUrl = this.config.getServerURL() + this.databaseName + '/' + id;
    return CouchDbRepository.authorizeExecute(this.credentialsFactory, (options) => {
      return this.httpClient.get(queryUrl, options).pipe(
        catchError((err: HttpErrorResponse) => {
          if (err.error.error === 'not_found') {
            return of(null);
          } else {
            throw err;
          }
        }),
        concatMap((response: any) => {
          return response !== null ? of(this.factory(response)) : of(null);
        }),
      );
    });
  }

  remove$(id: string): Observable<boolean> {
    const getExistingEntityUrl = this.config.getServerURL() + this.databaseName + '/' + id;
    const postDeleteQueryUrl = this.config.getServerURL() + this.databaseName + '/_bulk_docs';
    return CouchDbRepository.authorizeExecute(this.credentialsFactory, (options) => {
      return this.httpClient.get(getExistingEntityUrl, options).pipe(
        concatMap((response: any) => {
          const doc = new CouchDbEntity();
          doc._deleted = true;
          doc._id = id;
          doc._rev = response._rev;
          const deletionQueryData = new CouchDbEntitiesList();
          deletionQueryData.docs = [doc];
          return this.httpClient.post(postDeleteQueryUrl, deletionQueryData, options).pipe(
            map((res: any) => {
              return Array.isArray(res) && res.length === 1 ? res[0].ok : false;
            }),
          );
        }),
      );
    });
  }

  find$(filter: CouchDbFilter<T>): Observable<T> {
    const queryUrl = this.config.getServerURL() + this.databaseName + '/_find';
    return CouchDbRepository.authorizeExecute(this.credentialsFactory, (options) => {
      return this.httpClient.post(queryUrl, filter.getMangoQuery(), options).pipe(
        mergeMap((response: any) => {
          if (new CouchDbEntitiesList().checkType(response)) {
            if (response.docs.length === 0) {
              return from([]);
            } else {
              return from(response.docs.map((doc) => this.factory(doc)));
            }
          } else {
            throw new Error('');
          }
        }),
      );
    });
  }

  public static authorizeExecute<R>(
    credentialsFactory: CredentialsFactory,
    func: (options: any) => Observable<R>,
    asAnonymous: boolean = false,
  ): Observable<R> {
    const createAuthorizationOptions = (c: Credentials) => {
      return {
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
          'Authorization': 'Basic ' + c.asEncodedString,
        }),
        observe: 'body',
        responseType: 'json',
      };
    };
    return asAnonymous
      ? func(createAuthorizationOptions(new Credentials('guest', '123')))
      : credentialsFactory.getCredentials$().pipe(mergeMap((_) => func(createAuthorizationOptions(_))));
  }
}
